import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, History, Play } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getChapterReview } from '@/features/chapters/api/chapterReview';
import { analyticsService } from '@/services/analyticsService';

import type { LearningSession } from '../types';
import { getBookTheme } from '../utils/bookTheme';

interface ChapterModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: LearningSession | null;
  onStartChapter: (sessionId: number, sectionId?: number) => void;
  isPremium?: boolean;
  position?: 'below' | 'above';
  canReadFreeSection?: boolean;
}

const ChapterModal: React.FC<ChapterModalProps> = ({
  isOpen,
  onClose,
  session,
  onStartChapter,
  isPremium = true,
  position = 'below',
  canReadFreeSection = false,
}) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!session) return null;

  const nodeSize = 96;
  const isCompleted = session.isCompleted;
  const isNewChapter = session.isActive && session.progress === 0;
  const isBookAnalysis = session.kind === 'book-analysis';
  const isLocked = session.isLocked;
  const theme = getBookTheme(session.bookId);

  const titleText = isBookAnalysis
    ? (session.bookTitle ?? session.title)
    : isCompleted
      ? 'مرور'
      : session.title;

  const subtitleText = isBookAnalysis
    ? (session.actionSubtitle ?? 'تحلیل کتاب')
    : isCompleted
      ? session.title
      : (session.displayTitle ?? session.nextSectionTitle ?? session.title);

  let buttonText = session.actionButtonText ?? 'شروع فصل';
  let ButtonIcon = Play;

  if (isBookAnalysis) {
    buttonText = session.actionButtonText ?? 'شروع تحلیل';
  } else if (isCompleted) {
    buttonText = 'مرور فصل';
    ButtonIcon = History;
  } else if (isNewChapter) {
    buttonText = 'شروع فصل جدید';
  } else if (session.isActive) {
    buttonText = 'ادامه مطالعه';
  }

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} />

          <div
            className="absolute z-50"
            style={position === 'above'
              ? { bottom: nodeSize + 10, left: '50%', transform: 'translateX(-50%)' }
              : { top: nodeSize + 10, left: '50%', transform: 'translateX(-50%)' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: position === 'above' ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative" dir="rtl">
                {position === 'above' ? (
                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-[10px]">
                    <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px]" style={{ borderTopColor: isLocked ? '#e5e7eb' : theme.primaryDark }} />
                  </div>
                ) : (
                  <div className="absolute left-1/2 -translate-x-1/2 -top-[10px]">
                    <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px]" style={{ borderBottomColor: isLocked ? '#e5e7eb' : theme.primary }} />
                  </div>
                )}

                <div
                  className={`rounded-2xl shadow-2xl px-4 py-3 overflow-hidden ${isLocked ? 'text-gray-600 border border-gray-200' : 'text-white'}`}
                  style={{
                    backgroundImage: isLocked ? 'none' : `linear-gradient(145deg, ${theme.primary}, ${theme.primaryDark})`,
                    backgroundColor: isLocked ? '#f3f4f6' : undefined,
                    width: 'max-content',
                    minWidth: '160px',
                    maxWidth: 'min(calc(100vw - 40px), 260px)',
                  }}
                >
                  <p className={`text-xs font-medium font-persian text-right mb-1.5 ${isLocked ? 'text-gray-500' : 'text-white/90'}`}>
                    {titleText}
                  </p>

                  <h2 className={`text-sm font-bold font-persian text-right mb-4 leading-relaxed break-words ${isLocked ? 'text-gray-700' : 'text-white'}`}>
                    {subtitleText}
                  </h2>

                  {isBookAnalysis && (
                    <div className="mb-3 w-full text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-white" />
                        <p className="text-[12px] font-persian text-white">
                          هشدار اسپویل
                        </p>
                      </div>
                    </div>
                  )}

                  <button
                      disabled={session.isLocked}
                      onClick={async () => {
                        if (session.isLocked) return;
                        if (!isPremium && !canReadFreeSection) {
                          void analyticsService.logEvent('subscription_purchase_button_click', {
                            book_id: String(session.bookId ?? 'unknown'),
                            book_title: session.bookTitle || 'unknown',
                            button_type: 'learning_continue_modal',
                          });
                          navigate('/premium-subscription');
                        } else if (isCompleted && !isBookAnalysis) {
                          const reviewData = await getChapterReview(session.id);
                          const sections = (reviewData.sections || []) as any[];
                          const isNovelReview = sections.some((s) => s?.section_type === 'novel' || Array.isArray(s?.content?.paragraphs));

                          if (isNovelReview) {
                            const localBookId = Number(localStorage.getItem('lastLearningPathBookId') || 0);
                            const resolvedBookId = (session as any)?.bookId || localBookId || 0;
                            const reviewSections = sections.map((section) => ({
                              sectionId: section.section_id,
                              sectionTitle: section.title || 'بخش داستان',
                              paragraphs: (section.content?.paragraphs || []).map((p: any) => ({
                                index: p.index,
                                content: p.content,
                                reactions: p.reactions || [],
                              })),
                            }));

                            navigate('/novel-read', {
                              state: {
                                bookId: resolvedBookId,
                                chapterId: session.id,
                                sectionId: reviewSections[0]?.sectionId || 0,
                                sectionTitle: reviewSections[0]?.sectionTitle || 'بخش داستان',
                                previous_section_summary: null,
                                trailer: null,
                                returnTo: '/review/result',
                                reviewMode: 'chapter_review_novel',
                                reviewSections,
                                content: { paragraphs: reviewSections[0]?.paragraphs || [] },
                              },
                            });
                            return;
                          }

                          navigate('/review', { state: { chapterId: session.id } });
                        } else {
                          onStartChapter(
                            session.id,
                            (session as any)?.nextSection?.section_id ?? (session as any)?.nextSectionId
                          );
                        }
                      }}
                      className={`w-full font-bold text-sm py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5 ${isLocked ? 'bg-gray-200 text-gray-500' : 'bg-white hover:bg-gray-50'}`}
                      style={{ color: isLocked ? '#6b7280' : theme.primary }}
                    >
                      {session.isLocked ? (
                        <span className="font-persian">شروع فصل جدید</span>
                      ) : isPremium || canReadFreeSection ? (
                        <>
                          <ButtonIcon className="w-4 h-4" fill={isCompleted && !isBookAnalysis ? 'none' : 'currentColor'} />
                          <span className="font-persian">{buttonText}</span>
                        </>
                      ) : (
                        <span className="font-persian">خرید اشتراک</span>
                      )}
                    </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default React.memo(ChapterModal);
